class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();
    if (this.closest('[data-coins-global-search]')) return;
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);

    this.debouncedOnSubmit = debounce((event) => {
      this.onSubmitHandler(event);
    }, 800);

    const facetForm = this.querySelector('form');
    facetForm.addEventListener('input', this.debouncedOnSubmit.bind(this));

    const facetWrapper = this.querySelector('#FacetsWrapperDesktop');
    if (facetWrapper) facetWrapper.addEventListener('keyup', onKeyUpEscape);
  }

  static setListeners() {
    if (document.querySelector('[data-coins-global-search]')) return;
    const onHistoryChange = (event) => {
      const searchParams = event.state ? event.state.searchParams : FacetFiltersForm.searchParamsInitial;
      if (searchParams === FacetFiltersForm.searchParamsPrev) return;
      FacetFiltersForm.renderPage(searchParams, null, false);
    };
    window.addEventListener('popstate', onHistoryChange);
  }

  static toggleActiveFacets(disable = true) {
    document.querySelectorAll('.js-facet-remove').forEach((element) => {
      element.classList.toggle('disabled', disable);
    });
  }

  static renderPage(searchParams, event, updateURLHash = true) {
    if (document.querySelector('[data-coins-native-search]')) {
      const nativeParams = new URLSearchParams(searchParams);
      nativeParams.set('view', 'native');
      searchParams = nativeParams.toString();
    }
    FacetFiltersForm.searchParamsPrev = searchParams;
    const sections = FacetFiltersForm.getSections();
    const updateEvent = FacetFiltersForm.startUpdateEvent(searchParams);
    const countContainer = document.getElementById('ProductCount');
    const countContainerDesktop = document.getElementById('ProductCountDesktop');
    const loadingSpinners = document.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form .loading__spinner',
    );
    loadingSpinners.forEach((spinner) => spinner.classList.remove('hidden'));
    document.getElementById('ProductGridContainer').querySelector('.collection').classList.add('loading');
    if (countContainer) {
      countContainer.classList.add('loading');
    }
    if (countContainerDesktop) {
      countContainerDesktop.classList.add('loading');
    }

    sections.forEach((section) => {
      const url = `${window.location.pathname}?section_id=${section.section}&${searchParams}`;
      const filterDataUrl = (element) => element.url === url;

      FacetFiltersForm.filterData.some(filterDataUrl)
        ? FacetFiltersForm.renderSectionFromCache(filterDataUrl, event, updateEvent)
        : FacetFiltersForm.renderSectionFromFetch(url, event, updateEvent);
    });

    if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams);
  }

  static startUpdateEvent(searchParams) {
    const { SearchUpdateEvent, CollectionUpdateEvent } = window.StandardEvents || {};
    if (!SearchUpdateEvent && !CollectionUpdateEvent) return;

    const facetsForm = document.querySelector('facet-filters-form');
    const facetsContainer = document.querySelector('.facets-container');
    const collectionId = facetsContainer?.dataset.collectionId || null;
    const collectionHandle = facetsContainer?.dataset.collectionHandle || '';
    const currentCount = parseInt(document.getElementById('ProductCount')?.dataset.productCount) || 0;
    const urlSearchParams = new URLSearchParams(searchParams);
    const isSearchPage = facetsContainer?.dataset.template === 'search';
    const isCollectionPage = facetsContainer?.dataset.template === 'collection';
    const dispatchTarget = facetsForm || document;

    let deferred;

    if (isSearchPage) {
      deferred = SearchUpdateEvent.createPromise();
      dispatchTarget.dispatchEvent(
        new SearchUpdateEvent({
          search: {
            query: urlSearchParams.get('q') || '',
            productFilters: SearchUpdateEvent.parseProductFilters(urlSearchParams),
            sortKey: SearchUpdateEvent.getSortKey(urlSearchParams),
          },
          promise: deferred.promise,
        }),
      );
    } else if (isCollectionPage) {
      // collectionId is null for auto-generated collections (/collections/all,
      // /collections/vendors/<x>, etc.) — the SSE schema accepts that.
      deferred = CollectionUpdateEvent.createPromise();
      dispatchTarget.dispatchEvent(
        new CollectionUpdateEvent({
          collection: { id: collectionId, handle: collectionHandle, productsCount: currentCount },
          productFilters: CollectionUpdateEvent.parseProductFilters(urlSearchParams),
          sortKey: CollectionUpdateEvent.getSortKey(urlSearchParams),
          promise: deferred.promise,
        }),
      );
    }

    if (!deferred) return;

    return {
      resolve: (filteredCount) => {
        deferred.resolve({ [isSearchPage ? 'totalCount' : 'productsCount']: filteredCount });
      },
      reject: deferred.reject,
    };
  }

  static renderSectionFromFetch(url, event, updateEvent) {
    fetch(url)
      .then((response) => response.text())
      .then((html) => {
        FacetFiltersForm.filterData = [...FacetFiltersForm.filterData, { html, url }];
        FacetFiltersForm.renderSection(html, event, updateEvent);
      })
      .catch((error) => {
        console.error(error);
        updateEvent?.reject(error);
      });
  }

  static renderSectionFromCache(filterDataUrl, event, updateEvent) {
    const html = FacetFiltersForm.filterData.find(filterDataUrl).html;
    FacetFiltersForm.renderSection(html, event, updateEvent);
  }

  static renderSection(html, event, updateEvent) {
    FacetFiltersForm.renderFilters(html, event);
    FacetFiltersForm.renderProductGridContainer(html);
    FacetFiltersForm.renderProductCount(html, updateEvent);
    if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger(html.innerHTML);
  }

  static renderProductGridContainer(html) {
    document.getElementById('ProductGridContainer').innerHTML = new DOMParser()
      .parseFromString(html, 'text/html')
      .getElementById('ProductGridContainer').innerHTML;

    document
      .getElementById('ProductGridContainer')
      .querySelectorAll('.scroll-trigger')
      .forEach((element) => {
        element.classList.add('scroll-trigger--cancel');
      });
  }

  static renderProductCount(html, updateEvent) {
    const parsedHtml = new DOMParser().parseFromString(html, 'text/html');
    const sourceCount = parsedHtml.getElementById('ProductCount');
    const count = sourceCount.innerHTML;
    const container = document.getElementById('ProductCount');
    const containerDesktop = document.getElementById('ProductCountDesktop');
    container.innerHTML = count;
    container.dataset.productCount = sourceCount.dataset.productCount || '';
    container.dataset.totalCount = sourceCount.dataset.totalCount || '';
    container.classList.remove('loading');
    if (containerDesktop) {
      containerDesktop.innerHTML = count;
      containerDesktop.classList.remove('loading');
    }
    const loadingSpinners = document.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form .loading__spinner',
    );
    loadingSpinners.forEach((spinner) => spinner.classList.add('hidden'));

    updateEvent?.resolve(parseInt(sourceCount.dataset.productCount) || 0);
  }

  static renderFilters(html, event) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
    const facetDetailsElementsFromFetch = parsedHTML.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter',
    );
    const facetDetailsElementsFromDom = document.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter',
    );

    // Remove facets that are no longer returned from the server
    Array.from(facetDetailsElementsFromDom).forEach((currentElement) => {
      if (!Array.from(facetDetailsElementsFromFetch).some(({ id }) => currentElement.id === id)) {
        currentElement.remove();
      }
    });

    const matchesId = (element) => {
      const jsFilter = event ? event.target.closest('.js-filter') : undefined;
      return jsFilter ? element.id === jsFilter.id : false;
    };

    const facetsToRender = Array.from(facetDetailsElementsFromFetch).filter((element) => !matchesId(element));
    const countsToRender = Array.from(facetDetailsElementsFromFetch).find(matchesId);

    facetsToRender.forEach((elementToRender, index) => {
      const currentElement = document.getElementById(elementToRender.id);
      // Element already rendered in the DOM so just update the innerHTML
      if (currentElement) {
        document.getElementById(elementToRender.id).innerHTML = elementToRender.innerHTML;
      } else {
        if (index > 0) {
          const { className: previousElementClassName, id: previousElementId } = facetsToRender[index - 1];
          // Same facet type (eg horizontal/vertical or drawer/mobile)
          if (elementToRender.className === previousElementClassName) {
            document.getElementById(previousElementId).after(elementToRender);
            return;
          }
        }

        if (elementToRender.parentElement) {
          document.querySelector(`#${elementToRender.parentElement.id} .js-filter`).before(elementToRender);
        }
      }
    });

    FacetFiltersForm.renderActiveFacets(parsedHTML);
    FacetFiltersForm.renderAdditionalElements(parsedHTML);

    if (countsToRender) {
      const closestJSFilterID = event.target.closest('.js-filter').id;

      if (closestJSFilterID) {
        FacetFiltersForm.renderCounts(countsToRender, event.target.closest('.js-filter'));
        FacetFiltersForm.renderMobileCounts(countsToRender, document.getElementById(closestJSFilterID));

        const newFacetDetailsElement = document.getElementById(closestJSFilterID);

        const isTextInput = event.target.getAttribute('type') === 'text';

        if (!isTextInput) {
          // Try to return focus to the same checkbox the user just toggled,
          // re-selecting it from the freshly rendered HTML by its id.
          const originatingInputId = event.target.id;
          const matchingInput = originatingInputId
            ? newFacetDetailsElement.querySelector(`#${CSS.escape(originatingInputId)}`)
            : null;

          if (matchingInput) {
            matchingInput.focus();
          } else {
            // Fallback to summary/close button if the checkbox can't be found
            const fallbackSelector = newFacetDetailsElement.classList.contains('mobile-facets__details')
              ? `.mobile-facets__close-button`
              : `.facets__summary`;
            const fallbackElement = newFacetDetailsElement.querySelector(fallbackSelector);
            if (fallbackElement) fallbackElement.focus();
          }
        }
      }
    }
  }

  static renderActiveFacets(html) {
    const activeFacetElementSelectors = ['.active-facets-mobile', '.active-facets-desktop'];

    activeFacetElementSelectors.forEach((selector) => {
      const activeFacetsElement = html.querySelector(selector);
      if (!activeFacetsElement) return;
      document.querySelector(selector).innerHTML = activeFacetsElement.innerHTML;
    });

    FacetFiltersForm.toggleActiveFacets(false);
  }

  static renderAdditionalElements(html) {
    const mobileElementSelectors = ['.mobile-facets__open', '.mobile-facets__count', '.sorting'];

    mobileElementSelectors.forEach((selector) => {
      if (!html.querySelector(selector)) return;
      document.querySelector(selector).innerHTML = html.querySelector(selector).innerHTML;
    });

    document.getElementById('FacetFiltersFormMobile').closest('menu-drawer').bindEvents();
  }

  static renderCounts(source, target) {
    const targetSummary = target.querySelector('.facets__summary');
    const sourceSummary = source.querySelector('.facets__summary');

    if (sourceSummary && targetSummary) {
      targetSummary.outerHTML = sourceSummary.outerHTML;
    }

    const targetHeaderElement = target.querySelector('.facets__header');
    const sourceHeaderElement = source.querySelector('.facets__header');

    if (sourceHeaderElement && targetHeaderElement) {
      targetHeaderElement.outerHTML = sourceHeaderElement.outerHTML;
    }

    const targetWrapElement = target.querySelector('.facets-wrap');
    const sourceWrapElement = source.querySelector('.facets-wrap');

    if (sourceWrapElement && targetWrapElement) {
      const isShowingMore = Boolean(target.querySelector('show-more-button .label-show-more.hidden'));
      if (isShowingMore) {
        sourceWrapElement
          .querySelectorAll('.facets__item.hidden')
          .forEach((hiddenItem) => hiddenItem.classList.replace('hidden', 'show-more-item'));
      }

      targetWrapElement.outerHTML = sourceWrapElement.outerHTML;
    }
  }

  static renderMobileCounts(source, target) {
    const targetFacetsList = target.querySelector('.mobile-facets__list');
    const sourceFacetsList = source.querySelector('.mobile-facets__list');

    if (sourceFacetsList && targetFacetsList) {
      targetFacetsList.outerHTML = sourceFacetsList.outerHTML;
    }
  }

  static updateURLHash(searchParams) {
    history.pushState({ searchParams }, '', `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`);
  }

  static getSections() {
    return [
      {
        section: document.getElementById('product-grid').dataset.id,
      },
    ];
  }

  createSearchParams(form) {
    const formData = new FormData(form);
    return new URLSearchParams(formData).toString();
  }

  onSubmitForm(searchParams, event) {
    FacetFiltersForm.renderPage(searchParams, event);
  }

  onSubmitHandler(event) {
    event.preventDefault();
    const sortFilterForms = document.querySelectorAll('facet-filters-form form');
    if (event.srcElement.className == 'mobile-facets__checkbox') {
      const searchParams = this.createSearchParams(event.target.closest('form'));
      this.onSubmitForm(searchParams, event);
    } else {
      const forms = [];
      const isMobile = event.target.closest('form').id === 'FacetFiltersFormMobile';

      sortFilterForms.forEach((form) => {
        if (!isMobile) {
          if (form.id === 'FacetSortForm' || form.id === 'FacetFiltersForm' || form.id === 'FacetSortDrawerForm') {
            forms.push(this.createSearchParams(form));
          }
        } else if (form.id === 'FacetFiltersFormMobile') {
          forms.push(this.createSearchParams(form));
        }
      });
      this.onSubmitForm(forms.join('&'), event);
    }
  }

  onActiveFilterClick(event) {
    event.preventDefault();
    FacetFiltersForm.toggleActiveFacets();
    const url =
      event.currentTarget.href.indexOf('?') == -1
        ? ''
        : event.currentTarget.href.slice(event.currentTarget.href.indexOf('?') + 1);
    FacetFiltersForm.renderPage(url);
  }
}

FacetFiltersForm.filterData = [];
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
customElements.define('facet-filters-form', FacetFiltersForm);
FacetFiltersForm.setListeners();

class PriceRange extends HTMLElement {
  constructor() {
    super();
    this.querySelectorAll('input').forEach((element) => {
      element.addEventListener('change', this.onRangeChange.bind(this));
      element.addEventListener('keydown', this.onKeyDown.bind(this));
    });
    this.setMinAndMaxValues();
  }

  onRangeChange(event) {
    this.adjustToValidValues(event.currentTarget);
    this.setMinAndMaxValues();
  }

  onKeyDown(event) {
    if (event.metaKey) return;

    const pattern = /[0-9]|\.|,|'| |Tab|Backspace|Enter|ArrowUp|ArrowDown|ArrowLeft|ArrowRight|Delete|Escape/;
    if (!event.key.match(pattern)) event.preventDefault();
  }

  setMinAndMaxValues() {
    const inputs = this.querySelectorAll('input');
    const minInput = inputs[0];
    const maxInput = inputs[1];
    if (maxInput.value) minInput.setAttribute('data-max', maxInput.value);
    if (minInput.value) maxInput.setAttribute('data-min', minInput.value);
    if (minInput.value === '') maxInput.setAttribute('data-min', 0);
    if (maxInput.value === '') minInput.setAttribute('data-max', maxInput.getAttribute('data-max'));
  }

  adjustToValidValues(input) {
    const value = Number(input.value);
    const min = Number(input.getAttribute('data-min'));
    const max = Number(input.getAttribute('data-max'));

    if (value < min) input.value = min;
    if (value > max) input.value = max;
  }
}

customElements.define('price-range', PriceRange);

class FacetRemove extends HTMLElement {
  constructor() {
    super();
    const facetLink = this.querySelector('a');
    facetLink.setAttribute('role', 'button');
    facetLink.addEventListener('click', this.closeFilter.bind(this));
    facetLink.addEventListener('keyup', (event) => {
      event.preventDefault();
      if (event.code.toUpperCase() === 'SPACE') this.closeFilter(event);
    });
  }

  closeFilter(event) {
    event.preventDefault();
    const form = this.closest('facet-filters-form') || document.querySelector('facet-filters-form');
    form.onActiveFilterClick(event);
  }
}

customElements.define('facet-remove', FacetRemove);


/* Coins2140 v2.1 — colección: contador dinámico y ordenación accesible */
(() => {
  const isCollection = window.location.pathname.startsWith('/collections/');
  const isSharedSearch = !!document.querySelector('.template-search.coins-collection-page');
  if (!isCollection && !isSharedSearch) return;

  if (isCollection) document.documentElement.classList.add('coins-collection-page');

  const desktopSortMedia = window.matchMedia('(min-width: 990px) and (hover: hover) and (pointer: fine)');
  let openSort = null;
  let sortId = 0;
  let refreshQueued = false;

  const updateCollectionCount = () => {
    const source = document.querySelector('#ProductCountDesktop');
    const target = document.querySelector('[data-coins-collection-count]');
    if (!source || !target) return;

    const match = source.textContent.trim().match(/\d[\d.\s\u00a0]*/);
    if (!match) return;

    const displayNumber = match[0].trim();
    const numericValue = Number(displayNumber.replace(/\D/g, ''));
    const nextText = displayNumber + ' ' + (numericValue === 1 ? 'moneda' : 'monedas');

    if (target.textContent.trim() !== nextText) target.textContent = nextText;
  };

  const closeSort = (sort, returnFocus = false) => {
    if (!sort) return;
    sort.dataset.open = 'false';
    sort.trigger.setAttribute('aria-expanded', 'false');
    if (returnFocus) sort.trigger.focus();
    if (openSort === sort) openSort = null;
  };

  const refreshSelectedOption = (sort) => {
    const selected = sort.nativeSelect.selectedOptions[0];
    sort.valueLabel.textContent = selected ? selected.textContent.trim() : '';
    sort.trigger.setAttribute('aria-label', 'Ordenar por: ' + sort.valueLabel.textContent);

    sort.options.forEach((option) => {
      option.setAttribute('aria-selected', String(option.dataset.value === sort.nativeSelect.value));
    });
  };

  const selectSortOption = (sort, option) => {
    if (sort.nativeSelect.value === option.dataset.value) {
      closeSort(sort, true);
      return;
    }

    sort.nativeSelect.value = option.dataset.value;
    refreshSelectedOption(sort);
    closeSort(sort, true);
    sort.nativeSelect.dispatchEvent(new Event('input', { bubbles: true }));
    sort.nativeSelect.dispatchEvent(new Event('change', { bubbles: true }));
  };

  const openSortMenu = (sort, focusLast = false) => {
    if (openSort && openSort !== sort) closeSort(openSort);
    sort.dataset.open = 'true';
    sort.trigger.setAttribute('aria-expanded', 'true');
    openSort = sort;

    const selectedIndex = sort.options.findIndex((option) => option.getAttribute('aria-selected') === 'true');
    const focusIndex = focusLast ? sort.options.length - 1 : Math.max(selectedIndex, 0);
    sort.options[focusIndex]?.focus();
  };

  const buildDesktopSort = (nativeSelect) => {
    if (!desktopSortMedia.matches || nativeSelect.dataset.coinsSortReady === 'true') return;

    const nativeField = nativeSelect.closest('.select');
    if (!nativeField) return;

    nativeSelect.dataset.coinsSortReady = 'true';
    nativeSelect.setAttribute('aria-hidden', 'true');
    nativeSelect.setAttribute('tabindex', '-1');
    nativeField.classList.add('coins-sort-native-field');

    sortId += 1;
    const listId = 'CoinsSortList-' + sortId;
    const valueId = 'CoinsSortValue-' + sortId;
    const shell = document.createElement('div');
    const trigger = document.createElement('button');
    const valueLabel = document.createElement('span');
    const chevron = document.createElement('span');
    const list = document.createElement('div');

    shell.className = 'coins-sort';
    shell.dataset.open = 'false';
    shell.nativeSelect = nativeSelect;
    shell.nativeField = nativeField;
    shell.trigger = trigger;
    shell.valueLabel = valueLabel;

    trigger.type = 'button';
    trigger.className = 'coins-sort__trigger';
    trigger.setAttribute('aria-haspopup', 'listbox');
    trigger.setAttribute('aria-expanded', 'false');
    trigger.setAttribute('aria-controls', listId);

    valueLabel.id = valueId;
    valueLabel.className = 'coins-sort__value';
    chevron.className = 'coins-sort__chevron';
    chevron.setAttribute('aria-hidden', 'true');

    const visibleLabel = document.querySelector('label[for="SortBy"]');
    if (visibleLabel?.id) {
      trigger.setAttribute('aria-labelledby', visibleLabel.id + ' ' + valueId);
    } else {
      trigger.setAttribute('aria-label', 'Ordenar por');
    }

    list.id = listId;
    list.className = 'coins-sort__list';
    list.setAttribute('role', 'listbox');
    list.setAttribute('aria-label', 'Opciones de ordenación');

    shell.options = Array.from(nativeSelect.options)
      .filter((option) => !option.disabled)
      .map((nativeOption) => {
        const option = document.createElement('button');
        const label = document.createElement('span');
        const check = document.createElement('span');

        option.type = 'button';
        option.className = 'coins-sort__option';
        option.dataset.value = nativeOption.value;
        option.setAttribute('role', 'option');
        option.setAttribute('tabindex', '-1');

        label.textContent = nativeOption.textContent.trim();
        check.className = 'coins-sort__check';
        check.textContent = '✓';
        check.setAttribute('aria-hidden', 'true');

        option.append(label, check);
        option.addEventListener('click', () => selectSortOption(shell, option));
        option.addEventListener('keydown', (event) => {
          const currentIndex = shell.options.indexOf(option);
          let nextIndex = null;

          if (event.key === 'ArrowDown') nextIndex = (currentIndex + 1) % shell.options.length;
          if (event.key === 'ArrowUp') nextIndex = (currentIndex - 1 + shell.options.length) % shell.options.length;
          if (event.key === 'Home') nextIndex = 0;
          if (event.key === 'End') nextIndex = shell.options.length - 1;

          if (nextIndex !== null) {
            event.preventDefault();
            shell.options[nextIndex].focus();
          } else if (event.key === 'Escape') {
            event.preventDefault();
            closeSort(shell, true);
          } else if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            selectSortOption(shell, option);
          } else if (event.key === 'Tab') {
            closeSort(shell);
          }
        });

        list.append(option);
        return option;
      });

    trigger.append(valueLabel, chevron);
    shell.append(trigger, list);
    nativeField.insertAdjacentElement('afterend', shell);
    refreshSelectedOption(shell);
    nativeSelect.addEventListener('coins:sort-sync', () => refreshSelectedOption(shell));

    trigger.addEventListener('click', () => {
      if (shell.dataset.open === 'true') closeSort(shell);
      else openSortMenu(shell);
    });

    trigger.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        event.preventDefault();
        openSortMenu(shell, event.key === 'ArrowUp');
      } else if (event.key === 'Escape') {
        closeSort(shell);
      }
    });
  };

  const teardownDesktopSorts = () => {
    document.querySelectorAll('.coins-sort').forEach((sort) => {
      sort.nativeField?.classList.remove('coins-sort-native-field');
      sort.nativeSelect?.removeAttribute('aria-hidden');
      sort.nativeSelect?.removeAttribute('tabindex');
      sort.nativeSelect?.removeAttribute('data-coins-sort-ready');
      sort.remove();
    });
    openSort = null;
  };

  const refreshCollectionControls = () => {
    refreshQueued = false;
    if (isCollection) updateCollectionCount();

    if (!desktopSortMedia.matches) {
      teardownDesktopSorts();
      return;
    }

    document
      .querySelectorAll('facet-filters-form.facets.small-hide select[name="sort_by"]')
      .forEach(buildDesktopSort);
  };

  const queueRefresh = () => {
    if (refreshQueued) return;
    refreshQueued = true;
    requestAnimationFrame(refreshCollectionControls);
  };

  document.addEventListener('click', (event) => {
    if (openSort && !openSort.contains(event.target)) closeSort(openSort);
  });

  desktopSortMedia.addEventListener('change', refreshCollectionControls);
  window.addEventListener('pageshow', queueRefresh);

  const gridContainer = document.querySelector('#ProductGridContainer');
  if (gridContainer) {
    new MutationObserver(queueRefresh).observe(gridContainer, {
      childList: true,
      subtree: true,
      characterData: true,
    });
  }

  refreshCollectionControls();
})();
